// The screens this app has. First generated from .appfactory/product-plan.json;
// edited since, so it is the app's own and `apply_plan` leaves it alone.
//
// Imported by the navigation shell rather than replacing it: the shell owns
// router.js and decides what to do with these.
//
// Today, Matches, Racing, History and Insights are the destinations in the tab
// bar. Settings is reached from the toolbar on them, the ticket from Today, a
// match from Matches, a race from Racing, Strategies from Settings, and sign-in
// from Settings.

// ONE SCREEN IS IMPORTED STATICALLY, AND IT IS THE ONE THE APP OPENS ON.
//
// Today is what boot has to paint, so Today is what boot is allowed to carry.
// Every other screen — including History, Settings, the ticket and sign-in,
// which were eager here until 2026-09-15 — is a dynamic import, because an
// eagerly imported screen nobody has opened is bytes spent on the first paint
// of a screen somebody IS looking at.
//
// History was the expensive one and not obviously so: it takes two text
// helpers from `lib/history-image.js`, which pulls in `pad-image.js` and
// `card-image.js` — the whole canvas card renderer, 21 KB of painters — so the
// share image for a screen nobody had opened was in the boot path. See the
// Performance section of HANDOFF.md.
import TodayPage from './pages/TodayPage.vue';

export const PRODUCT_ROUTES = [
  {
    id: 'today',
    path: '/',
    label: 'Today',
    component: TodayPage,
    primary: true,
    destination: true,
  },
  // The board itself, under everything the strategies say about it: the day's
  // fixtures, the best price on each side, and who is paying it. SECOND,
  // directly after Today, because it is the same day read one level down — a
  // punter who wants to know what is on tonight should not pass Strategies and
  // History to find out.
  //
  // Lazy for the reason the tabs below are: Today's shell is what
  // `scripts/ci-lighthouse.mjs` measures, and this screen is bytes nobody has
  // asked for until they tap the tab.
  {
    id: 'matches',
    path: '/matches',
    label: 'Matches',
    component: () => import('./pages/MatchesPage.vue'),
    primary: false,
    destination: true,
  },
  // THE THIRD DESTINATION, AND IT TOOK STRATEGIES'. The bar holds five and the
  // owner asked for racing; of the six candidates, Strategies is the one nobody
  // opens twice a day — it is where you go once to decide what prints, which is
  // a settings act — so it moved under Settings and its route stayed exactly
  // where it was. Racing sits after Matches because it is the same reading of
  // the same afternoon: the board, one level down, with the exchange beside it.
  //
  // Lazy, like every tab below Today: it pulls in the racing view, the runner
  // table and the log sheet for a screen nobody has asked for until they tap
  // the tab, and Today's shell is what `scripts/ci-lighthouse.mjs` measures.
  {
    id: 'racing',
    path: '/racing',
    label: 'Racing',
    component: () => import('./pages/RacingPage.vue'),
    primary: false,
    destination: true,
  },
  // Lazy, both of them. Today's shell is measured by `scripts/ci-lighthouse.mjs`
  // and the strategy editor drags in the whole engine; an eagerly imported
  // screen nobody has opened is bytes spent on the first paint of a screen
  // somebody IS looking at.
  //
  // NOT A DESTINATION ANY MORE, and the route is untouched — a flag hides a
  // destination, never a URL, and so does a demotion. `/strategies` is reached
  // from the row at the top of Settings' Today section, from Today's own
  // "How it works" and from every deep link that ever pointed at it. See
  // lib/flags.js.
  {
    id: 'strategies',
    path: '/strategies',
    label: 'Strategies',
    component: () => import('./pages/StrategiesPage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'history',
    path: '/history',
    label: 'History',
    component: () => import('./pages/HistoryPage.vue'),
    primary: false,
    destination: true,
  },
  // The fifth and last destination the pad can hold, AFTER History because it
  // is the reading OF that record rather than the record itself: somebody who
  // wants a particular ticket goes to History, somebody who wants to know
  // whether any of this is working comes here.
  //
  // Lazy, for the reason every tab below Today is: it pulls in five chart
  // components and the whole of the insights arithmetic for a screen nobody has
  // asked for until they tap the tab, and Today's shell is what
  // `scripts/ci-lighthouse.mjs` measures.
  {
    id: 'insights',
    path: '/insights',
    label: 'Insights',
    component: () => import('./pages/InsightsPage.vue'),
    primary: false,
    destination: true,
  },
  // Today's picks had a screen of its own: every strategy run over the board
  // and ranked against the market, reached from Today and from Strategies. The
  // ranking is on Today itself now — every strategy prints, in the order the
  // ranking put them — so the second reading of the same day has nowhere left
  // to be.
  //
  // Backtest is PUSHED, not a tab. It is something you do to a strategy, not a
  // place you go — and the pad already holds the five destinations it can.
  // Both routes are lazy: the result screen pulls in the whole engine, the
  // equity chart and the breakdown tables for a screen most opens never reach.
  {
    id: 'backtest',
    path: '/backtest',
    label: 'Backtest',
    component: () => import('./pages/BacktestPage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'backtest-result',
    path: '/backtest/:jobId',
    label: 'Result',
    component: () => import('./pages/BacktestResultPage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'strategy-editor',
    path: '/strategy/:id',
    label: 'Strategy',
    component: () => import('./pages/StrategyEditorPage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'multi-detail',
    path: '/multi/:id',
    label: 'Ticket',
    component: () => import('./pages/MultiDetailPage.vue'),
    primary: false,
    destination: false,
  },
  // One match with every bookmaker's price on it — PUSHED from the board, the
  // way the ticket is pushed from Today. Lazy with its tab: nobody reaches this
  // screen without first opening Matches, so the two chunks arrive together.
  {
    id: 'match',
    path: '/match/:key',
    label: 'Match',
    component: () => import('./pages/MatchPage.vue'),
    primary: false,
    destination: false,
  },
  // One race, every runner, and every book's price on each of them — PUSHED
  // from the Racing tab exactly as a match is pushed from the board. Lazy with
  // its tab, for the same reason: nobody reaches this screen without first
  // opening Racing, so the two chunks arrive together.
  //
  // `:matchKey` and not `:key`: Vue reserves `key` on a component, so with
  // `props: true` the router hands it to the renderer instead of to the page.
  // MatchPage works around that by reading the route; this one takes the prop.
  {
    id: 'race',
    path: '/race/:matchKey',
    label: 'Race',
    component: () => import('./pages/RacePage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'settings',
    path: '/settings',
    label: 'Settings',
    component: () => import('./pages/SettingsPage.vue'),
    primary: false,
    destination: false,
  },
  // Every word the app uses, in one place. Reached from Settings and from the
  // dotted terms in the help modals, which deep-link to an entry by hash
  // (`/glossary#clv`). Lazy for the reason the others are: it is forty-odd
  // definitions nobody has opened yet, and Today's shell is measured.
  {
    id: 'glossary',
    path: '/glossary',
    label: 'Glossary',
    component: () => import('./pages/GlossaryPage.vue'),
    primary: false,
    destination: false,
  },
  // Which boards the day's prices are fetched for. PUSHED from Settings, like
  // the glossary: the provider carries seventy-odd in-season leagues, which is
  // a screen with search and grouping rather than a list inside a settings
  // section — and the tab bar already holds the three destinations it can.
  // Lazy for the usual reason: it fetches a catalogue nobody asks for on a
  // first run, and Today's shell is what gets measured.
  {
    id: 'sports',
    path: '/sports',
    label: 'Sports',
    component: () => import('./pages/SportsPage.vue'),
    primary: false,
    destination: false,
  },
  {
    id: 'sign-in',
    path: '/sign-in',
    label: 'Sign in',
    component: () => import('./pages/SignInPage.vue'),
    primary: false,
    destination: false,
  },
];

export const PRIMARY_ROUTES = PRODUCT_ROUTES.filter((r) => r.primary);

// What the tab bar offers. A destination is a top-level screen someone
// navigates TO — never a detail screen and never settings.
export const DESTINATION_ROUTES = PRODUCT_ROUTES.filter((r) => r.destination).slice(0, 5);
export const HOME_ROUTE = (PRIMARY_ROUTES[0] ?? PRODUCT_ROUTES[0])?.path ?? '/';
